import { IconButton } from '@mui/material'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { downloadCSVFile, getCSVContent } from '../../../../utils/csv'
import PageLoading from '../../../../components/PageLoading/PageLoading'
import InformationTable from '../../../../components/Table/InformationTable'
import TableHeader from '../../../../components/TableHeader/TableHeader'
import { useNotification } from '../../../../context/NotificationManager/NotificationManager'
import MesRecienteSelector from '../../../../components/form/MesRecienteSelector/MesRecienteSelector'
import AcademiaSelector from '../../../../components/form/AcademiaSelector/AcademiaSelector'
import { getAuth } from '../../../../utils/auth'
import { dateToString, monthToString } from '../../../../utils/date'
import { textFilter } from '../../../../utils/table'
import { getEstado } from '../../../../utils/model/leads'
import {
  deleteLead,
  deleteLastContact,
  getLeadsCRM
} from '../../../../utils/api/leads'
import ModalConfirmacion from '../../../../modals/ModalConfirmacion/ModalConfirmacion'
import css from './Leads.module.css'

const LeadCerrado = () => <span className={css.cerrado}>Lead cerrado</span>

const Leads = () => {
  const notification = useNotification()
  const queryClient = useQueryClient()

  const [mes, setMes] = useState(monthToString(new Date()))
  const [centro, setCentro] = useState(getAuth().centro)

  const [search, setSearch] = useState('')
  const [modalDeleteLead, setModalDeleteLead] = useState(false)
  const [modalDeleteContact, setModalDeleteContact] = useState(false)
  const [leadIdToDelete, setLeadIdToDelete] = useState(null)

  const { isLoading: isLoadingLeads, data: leadList = [] } = useQuery({
    queryKey: ['feedback', 'list', 'CRM', mes, centro],
    queryFn: () =>
      getLeadsCRM({ mes, centro: centro.trim() })
        .then(datos => datos)
        .catch(err => {
          notification.error({
            title: 'Error al recuperar los leads',
            content: err
          })
          return []
        })
  })

  const { isFetching: isDeleting, mutate: borrarLead } = useMutation({
    mutationFn: deleteLead,
    onSuccess: () => {
      notification.success({
        title: 'Lead borrado',
        content: 'El lead ha sido borrado correctamente'
      })
      queryClient.invalidateQueries(['feedback', 'list', 'CRM', mes, centro])
      setModalDeleteLead(false)
      setLeadIdToDelete(null)
    },
    onError: err => {
      notification.error({ title: 'Error al borrar el lead', content: err })
      setModalDeleteLead(false)
      setLeadIdToDelete(null)
    }
  })

  const { isFetching: isDeletingContact, mutate: borrarUltimoContacto } =
    useMutation({
      mutationFn: deleteLastContact,
      onSuccess: () => {
        notification.success({
          title: 'Contacto borrado',
          content: 'El último contacto ha sido borrado correctamente'
        })
        queryClient.invalidateQueries(['feedback', 'list', 'CRM', mes, centro])
        setModalDeleteContact(false)
        setLeadIdToDelete(null)
      },
      onError: err => {
        notification.error({
          title: 'Error al borrar el contacto',
          content: err
        })
        setModalDeleteContact(false)
        setLeadIdToDelete(null)
      }
    })

  const handleExport = () => {
    const content = getCSVContent({
      columns: [
        'Nombre Lead',
        'Teléfono',
        'Email',
        'Total Contactos',
        'Próximo Contacto',
        'Estado Lead'
      ],
      data: leadList,
      transform: lead => [
        lead.contacto,
        lead.telefonos?.[0]?.telefono,
        lead.email,
        lead.contactos,
        lead.fecha ? 'Lead Cerrado' : dateToString(lead.fecha)
      ]
    })
    downloadCSVFile(content, 'leads-mes')
  }

  const handleOpenDeleteLeadModal = id => {
    setLeadIdToDelete(id)
    setModalDeleteLead(true)
  }

  const handleCloseDeleteLeadModal = () => {
    if (isDeleting) return
    setModalDeleteLead(false)
    setLeadIdToDelete(null)
  }

  const handleConfirmDeleteLead = () => {
    if (!leadIdToDelete || isDeleting) return
    borrarLead(leadIdToDelete)
  }

  const handleOpenDeleteContactModal = id => {
    setLeadIdToDelete(id)
    setModalDeleteContact(true)
  }

  const handleCloseDeleteContactModal = () => {
    if (isDeletingContact) return
    setModalDeleteContact(false)
    setLeadIdToDelete(null)
  }

  const handleConfirmDeleteContact = () => {
    if (!leadIdToDelete || isDeletingContact) return
    borrarUltimoContacto(leadIdToDelete)
  }

  const filterSearch = lead => {
    return textFilter({
      object: {
        ...lead,
        telefono: (lead.telefonos || []).map(t => t.telefono).join(' ')
      },
      fields: ['contacto', 'telefono', 'email', 'estado'],
      search: search
    })
  }

  return (
    <PageLoading isLoading={isLoadingLeads}>
      <TableHeader
        actions={
          <div className={css.actions}>
            <IconButton
              className={css.download}
              size='small'
              color='primary'
              onClick={handleExport}
            >
              <i className='material-icons'>download</i>
            </IconButton>
            <AcademiaSelector
              name='academia'
              label='Academia'
              value={centro}
              showNoOption
              onChange={e => setCentro(e.target.value)}
            />
            <MesRecienteSelector
              className={css.mes}
              name='mes'
              label='Mes'
              value={mes}
              onChange={e => setMes(e.target.value)}
            />
          </div>
        }
        title='Leads entrantes'
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
      />
      <InformationTable
        details={[
          { title: 'Lead', key: 'lead', align: 'left' },
          {
            title: 'Teléfono',
            key: 'telefono',
            sortDisabled: true,
            align: 'left'
          },
          { title: 'Email', key: 'email', sortDisabled: true, align: 'left' },
          {
            title: 'Contactos',
            key: 'contactos',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Próximo contacto',
            key: 'fecha',
            sortDisabled: true,
            align: 'left'
          },
          { title: 'Estado', key: 'estado', align: 'left' },
          {
            title: 'Opciones',
            key: 'opciones',
            sortDisabled: true,
            align: 'center'
          }
        ]}
        data={leadList.filter(filterSearch).map(lead => ({
          lead: lead.contacto,
          telefono: lead.telefonos?.[0]?.telefono,
          email: lead.email,
          contactos: lead.contactos,
          fecha: lead.fecha ? dateToString(lead.fecha) : <LeadCerrado />,
          estado: <strong>{getEstado(lead.estado)}</strong>,
          opciones: (
            <>
              <IconButton
                size='small'
                color='warning'
                onClick={() => handleOpenDeleteContactModal(lead.id)}
                title='Borrar último contacto'
              >
                <i className='material-icons'>history</i>
              </IconButton>
              <IconButton
                size='small'
                color='error'
                onClick={() => handleOpenDeleteLeadModal(lead.id)}
                title='Borrar lead'
              >
                <i className='material-icons'>delete</i>
              </IconButton>
            </>
          )
        }))}
      />
      <ModalConfirmacion
        open={modalDeleteLead}
        onClose={handleCloseDeleteLeadModal}
        onClick={handleConfirmDeleteLead}
        title='Confirmar borrado de lead'
        content={
          <>
            <p>Estás a punto de borrar este lead</p>
            <p>
              Esta operación es irreversible. Desaparecerá del CRM y no se podrá
              recuperar la información
            </p>
            <p>
              No computará a efectos de métricas de captación ni conversión.
            </p>
          </>
        }
        okText='Borrar lead'
      />
      <ModalConfirmacion
        open={modalDeleteContact}
        onClose={handleCloseDeleteContactModal}
        onClick={handleConfirmDeleteContact}
        title='Confirmar borrado del último contacto'
        content={
          <>
            <p>
              Estás a punto de borrar el último contacto registrado para este
              lead
            </p>
            <p>
              Esta operación es irreversible, y el lead volverá a su estado
              anterior. Afectando, por tanto a las métricas de captación y
              conversión, y puede alterar la planificación en aula si se borra
              una prueba
            </p>
          </>
        }
        okText='Borrar contacto'
      />
    </PageLoading>
  )
}

export default Leads
